/**
 * CaseTime — one timestamp, rendered in Europe/Berlin, relative by default.
 *
 * @description
 *   The single display primitive for any timestamp a human reads. Renders
 *   relative time ("vor 3 Minuten", "Gestern") and switches to the absolute
 *   Europe/Berlin value on click. The absolute value is ALWAYS carried in
 *   `title`, and on the interactive variant in the accessible name too, so
 *   hover and (there) screen readers get it without activating anything —
 *   see the a11y note under @props. Parsing goes through `utils/time.js`
 *   `parseTimestamp`, which reads an offsetless backend string as UTC instead
 *   of as browser-local — the bug this component exists to stop repeating.
 *   Solves the anti-pattern of every surface hand-rolling
 *   `new Date(x).toLocaleString(...)`, which renders in the VIEWER's timezone
 *   and mis-parses offsetless values.
 *
 * @useWhen
 *   - Any timestamp shown to staff: timeline entry, case detail, list cell
 *   - A "when did this happen" reading where relative time is the useful one
 *   - A due DATE or invoice date (pass `dateOnly` / `variant="absolute"`)
 *   - Replacing a bare `new Date(...).toLocaleString('de-DE', ...)` call site
 *
 * @propsRubric
 *   Which combination, decided by the SHAPE of the value and the JOB of the
 *   reading — derived across ~30 call sites in A5-4b, so it is not re-derived
 *   per file. Check the model, not the field NAME: `deadline_date` is an
 *   instant, `order_date` is a date column.
 *
 *   | value                          | reading                | props |
 *   |--------------------------------|------------------------|-------|
 *   | DB `date` column               | a due date / a day     | `dateOnly variant="absolute"` |
 *   | instant (`UtcDateTime`)        | day-granular row       | `variant="absolute" precision="date"` |
 *   | instant                        | date+time row          | `variant="absolute"` |
 *   | instant                        | "how stale is this?"   | default `auto` — SEE OVERRIDE |
 *
 *   ⚠️ ROW 4 IS A PRODUCT DECISION PER SITE, NOT A LANGUAGE QUESTION. The
 *   language was settled on 2026-08-01: UI language is ENGLISH uniformly,
 *   except LLM text (`utils/format.js:5`), and `relativeLocale="en"` implements
 *   it. A5-4b converted zero sites to `auto` while the Owner-Punkt was open —
 *   including ones that fit row 4 on the merits. `RecommendationCard`'s "Last
 *   checked:" is the clearest: a staleness reading by intent, shipped
 *   `variant="absolute"` because flipping it THEN would have been a language
 *   change dressed as a refactor. That reason is gone; a site converting today
 *   passes `relativeLocale="en"` and reads English. What still needs an answer
 *   is "does a relative staleness reading add value HERE?" — decided per
 *   surface, not by a global rule. The two live `auto` call sites are A5-4a's
 *   (`ActivityEntryRow`; `CaseInfoPanel`'s "Last Updated" entfiel mit der Datei
 *   am 2026-09-08).
 *
 *   Row 4 describes the steady state to move toward, not what the code does
 *   today. Written this way rather than deleted, because a rubric that silently
 *   omits the staleness case would let the next session infer that absolute is
 *   always right.
 *
 *   Two rules that decided most of the ambiguous cases:
 *   1. A HISTORICAL RECORD stays absolute. An audit row, a closed-at, an
 *      opened-at, an evidence upload — the timestamp IS the fact. Re-measuring
 *      a fixed past fact against a moving `now` on every render is worth less
 *      to someone reading the trail a week later than the value itself.
 *   2. A DEADLINE stays absolute. "in 3 Tagen" answers a different question
 *      than the one an operator scanning a due-date column is asking.
 *
 * @dontUseFor
 *   - Editable date input — use MUI DatePicker / a TextField `type="date"`
 *   - A duration or countdown ("3d left") — that is domain text, build it in
 *     the feature (see `rma/lib/slaSummary.js` getSlaChips)
 *   - Sorting or comparison — import `parseTimestamp` from `utils/time.js`
 *     directly; this component renders, it does not compute
 *   - Inside an already-clickable row with `variant="auto"` — a nested button
 *     is invalid; pass `variant="relative"` there
 *
 * @related
 *   - utils/time.js — parseTimestamp + the formatBerlin* family it renders with
 *   - ActivityEntryRow — the timeline row that renders one CaseTime per entry
 *   - rma/lib/slaSummary.js — SLA chip text; shares the same central parser
 *
 * @reuseHints
 *   - PO detail header dates (order_date, expected_arrival) — pass `dateOnly`
 *   - Admin job / cron "last run" cells, replacing the local formatRelative helpers
 *   - The ~100 `toLocale*` sites outside `src/rma/` (PO, Products, Ops, Admin,
 *     Market). Cleanup, not a bug fix — they already inherit the repaired
 *     parser through `formatBerlin*` where they use it.
 *
 * @dontUseYet
 *   ⚠️ LIFTED 2026-08-01 — this is a CONVERSION BACKLOG, not a language
 *   prohibition. Three live sites still hand-roll ENGLISH relative wording; they
 *   are the places to move onto `CaseTime relativeLocale="en"` (rows 4 and 5
 *   below are gone). The Owner settled the language question on 2026-08-01: UI language
 *   is ENGLISH uniformly, except LLM text (`utils/format.js:5`; restated as the
 *   house rule in `rma/hooks/useCaseLive.js`). What is open is only WHO converts
 *   them — G12b/C4 first. Converting a site now does NOT spread a two-language
 *   collision; it removes one, because the German default on a `lang="en"`
 *   document is the pre-existing deviation.
 *
 *   1. `rma/utils/relativeTime.js` — "14 min ago" / "3 h ago" / "5 d ago"
 *   2. `rma/RMAInbox.jsx` `formatRelative` — same
 *   3. `rma/timeline/conversationView.js` `formatDayLabel` — "Today, 30. Juli"
 *   4. ~~`rma/RMADetail.jsx` ×2 `formatDistanceToNow`~~ — entfallen 2026-09-08 (Block 2 Stufe 2)
 *   5. ~~`rma/components/CaseStatusBar.jsx` `getKeyFacts` ×3~~ — entfallen
 *      2026-09-08, mit der Datei geloescht (Block 2 Stufe 3a)
 *
 *   Site 5 was the sharpest while it existed: it sat in the SAME `StatCell` row
 *   A5-4b converted to German-formatted absolute dates, so one box read
 *   `SLA Due 19.04.2026` next to `Last Reply 3d ago`.
 *
 *   PARSE AND WORDING ARE SEPARABLE, and A5-4b separated them at sites 3, 4 and
 *   5: each had its offsetless-timestamp PARSE fixed (which instant the value
 *   names is not a language question) while its WORDING was left alone. Only
 *   sites 1 and 2 are untouched entirely.
 *
 * @props
 *   value — Date | ISO string | unix ms. Offsetless strings are read as UTC.
 *   variant — "auto" (default: relative, click toggles to absolute; renders
 *             static once the relative reading has already degraded to the
 *             absolute one, so there is never a toggle that does nothing) |
 *             "relative" (pinned relative, non-interactive) |
 *             "absolute" (pinned absolute, non-interactive)
 *   dateOnly — the VALUE is a calendar DATE (a DB date column): no time of day
 *             is ever rendered or fabricated, and the day never shifts.
 *             Implies `precision="date"` and forces the static absolute
 *             rendering under `variant="auto"`.
 *   precision — "datetime" (default) | "date". A DISPLAY choice, orthogonal to
 *             `dateOnly`: use `precision="date"` for a real INSTANT that should
 *             read as a day ("Case created", an SLA reminder row). It renders
 *             the BERLIN calendar day of that instant — which is what `dateOnly`
 *             would get wrong, since `dateOnly` pins the day named by the
 *             string itself (UTC), not the day it falls on in Berlin.
 *             Affects the absolute rendering only.
 *             **Pair it with `variant="absolute"`.** Combined with the default
 *             `auto` it yields a toggle whose "absolute" side is LESS precise
 *             than the relative one, so activating it loses the time of day —
 *             an information-losing control, the same anti-pattern the `auto`
 *             degradation above exists to avoid. Not reachable from any call
 *             site today; documented so it stays that way.
 *   withSeconds — include seconds in the absolute rendering.
 *   placeholder — text for a missing / unparseable value (default "—").
 *   now — reference instant (ms) for the relative reading; a pin seam for
 *             tests and for a parent that renders many rows against one clock.
 *   relativeLocale — "de" (default) | "en". The WORD SET of the RELATIVE
 *             reading only: "vor 3 Minuten" / "Gestern" vs "3 minutes ago" /
 *             "Yesterday". 🔴 The ABSOLUTE formats stay `de-DE` in both cases
 *             (`11.05.2026, 21:37`) — that is why the prop is named
 *             `relativeLocale` and not `locale` (G12-V, Grill W5). `'de'`
 *             reproduces the pre-G12 output byte for byte, so every existing
 *             call site is unchanged. English is opt-in, first used by the
 *             `/rma/faelligkeit` surface (G12b).
 *   sx — merged onto the rendered element.
 *   ...rest — spread onto the rendered element (data-testid, className, ...).
 *
 * @a11y
 *   The interactive variant is a real `<button type="button">` carrying
 *   `aria-pressed` — the state indicator matters because activating it makes
 *   the accessible name SHRINK (relative + absolute -> absolute alone), and
 *   without it a screen-reader user gets no confirmation anything happened.
 *   The pinned variants render a non-interactive `<time datetime=…>` and
 *   deliberately carry NO `aria-label`: `<time>` maps to the `generic` ARIA
 *   role, where `aria-label` is prohibited and is ignored by NVDA/VoiceOver
 *   anyway. Consequence, stated plainly: under `variant="relative"` the
 *   absolute value is `title`-only and NOT reachable by assistive tech. Use
 *   `variant="absolute"` (or `precision="date"`) at any call site where AT
 *   parity with sighted hover matters.
 */

import React, { useState } from 'react';
import { Box } from '@mui/material';
import { focusRing, hoverOnly } from '../../../theme/tokens';
import {
  berlinDateISO,
  formatBerlinDate,
  formatBerlinDateTime,
  formatBerlinRelative,
  parseTimestamp,
} from '../../../utils/time';

// At-rest affordance + focus ring, matching the established interactive-text
// pattern in `StatTile.jsx` (dotted underline at rest, solid on hover, 2px
// focus-visible ring). Reused rather than reinvented so a clickable timestamp
// looks like every other clickable value in the app.
//
// On the vertical padding, with the arithmetic actually done rather than
// asserted: at the live call site the inherited font is `FS.md` (12 px) with
// MUI's 1.5 line-height, so the line box is 18 px. `py: 0.25` (2 px a side)
// would give 22 px — UNDER the WCAG 2.5.8 minimum, which an earlier version of
// this comment claimed to meet. `py: 0.375` (3 px a side) reaches exactly 24 px
// there. The negative `my` cancels it for LAYOUT only — padding still grows the
// border box, so the hit area grows while the row's rhythm does not.
//
// The compliance argument does not rest on that number, and should not: this is
// a target inside a line of text, so WCAG 2.5.8's *Inline* exception applies and
// the size is constrained by surrounding non-target text by design. A call site
// with a smaller inherited font will land under 24 px again and still conform.
// The padding is a real improvement, not a guarantee — stated that way because
// the number does not survive being carried to another call site.
const TOGGLE_SX = {
  font: 'inherit',
  color: 'inherit',
  background: 'none',
  border: 0,
  px: 0,
  py: 0.375,
  mx: 0,
  my: -0.375,
  textAlign: 'inherit',
  cursor: 'pointer',
  borderRadius: 0.5,
  textDecoration: 'underline',
  textDecorationStyle: 'dotted',
  textUnderlineOffset: '3px',
  textDecorationColor: 'currentColor',
  transition: 'opacity 0.15s, text-decoration-style 0.15s',
  // K-5 (Welle 2): reine Wirkung ohne Hintergrund — gehoert hinter die Zeigerabfrage.
  ...hoverOnly({ textDecorationStyle: 'solid', opacity: 0.8 }),
  '&:focus-visible': focusRing(),
};

const STATIC_SX = { font: 'inherit', color: 'inherit' };

export default function CaseTime({
  value,
  variant = 'auto',
  dateOnly = false,
  precision = 'datetime',
  withSeconds = false,
  placeholder = '—',
  now,
  relativeLocale = 'de',
  sx,
  ...rest
}) {
  const [showAbsolute, setShowAbsolute] = useState(false);

  const parsed = parseTimestamp(value, { dateOnly });

  if (!parsed) {
    return (
      <Box component="span" sx={[STATIC_SX, ...(Array.isArray(sx) ? sx : [sx])]} {...rest}>
        {placeholder}
      </Box>
    );
  }

  // A calendar-date VALUE can only ever render to day precision.
  const dayPrecision = dateOnly || precision === 'date';
  const absolute = dayPrecision
    ? formatBerlinDate(parsed)
    : formatBerlinDateTime(parsed, { withSeconds });
  const relative = formatBerlinRelative(parsed, {
    dateOnly,
    locale: relativeLocale,
    ...(now != null ? { now } : {}),
  });

  // A calendar date has no instant to toggle to, so `auto` degrades to the
  // static absolute rendering rather than offering a click that changes little.
  const effective = dateOnly && variant === 'auto' ? 'absolute' : variant;
  // Past the relative window `formatBerlinRelative` already returns the
  // absolute string, so a toggle would swap a string for itself. Offer no
  // affordance rather than a control that does nothing.
  const interactive = effective === 'auto' && relative !== absolute;
  const display = effective === 'absolute' || (interactive && showAbsolute) ? absolute : relative;

  if (interactive) {
    // The absolute value is unconditionally in the accessible name, whether or
    // not it is the visible text — no activation required to reach it. Valid
    // here (and only here) because the element is a `button`, not a `generic`.
    const ariaLabel = display === absolute ? absolute : `${display} (${absolute})`;
    return (
      <Box
        component="button"
        type="button"
        onClick={() => setShowAbsolute((v) => !v)}
        title={absolute}
        aria-label={ariaLabel}
        aria-pressed={showAbsolute}
        sx={[TOGGLE_SX, ...(Array.isArray(sx) ? sx : [sx])]}
        {...rest}
      >
        {display}
      </Box>
    );
  }

  // A day-precision value must not claim a time of day it never had — per HTML
  // the `datetime` attribute of a calendar date is just the date. It has to be
  // the BERLIN day (what the text says), not the UTC day.
  const machineValue = dayPrecision ? berlinDateISO(parsed) : parsed.toISOString();

  // No `aria-label` here on purpose: `<time>` maps to the `generic` ARIA role,
  // which prohibits it. See the @a11y note in the header for the consequence.
  return (
    <Box
      component="time"
      dateTime={machineValue}
      title={absolute}
      sx={[STATIC_SX, ...(Array.isArray(sx) ? sx : [sx])]}
      {...rest}
    >
      {display}
    </Box>
  );
}
